import { Injectable } from '@angular/core';
import { openDB, IDBPDatabase } from 'idb';
import { FolhaServico } from '../../models/folha-servico';
import { Tarefa } from 'src/app/models/tarefa';
import { Jornada } from 'src/app/models/jornada';

@Injectable({
  providedIn: 'root'
})
export class StorageService {
  private dbPromise: Promise<IDBPDatabase>;

  constructor() {
    this.dbPromise = this.initDB();
  }

  private async initDB(): Promise<IDBPDatabase> {
    return openDB('InnovaDB', 13, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('tarefas')) {
          db.createObjectStore('tarefas', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('servidor')) {
          db.createObjectStore('servidor', { keyPath: 'id', autoIncrement: true });
        }
        if (!db.objectStoreNames.contains('jornadaServico')) {
          db.createObjectStore('jornadaServico', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('motoristaAtual')) {
          db.createObjectStore('motoristaAtual', { keyPath: 'id' }); 
        }
        if (!db.objectStoreNames.contains('folhasServico')) {
          db.createObjectStore('folhasServico', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('posicionamentos')) {
          db.createObjectStore('posicionamentos', { keyPath: 'id_local' });
        }
        if (!db.objectStoreNames.contains('controleRegistros')) {
          db.createObjectStore('controleRegistros', { keyPath: 'id' });
        }
      }
    });
  }

  // MÉTODOS RELACIONADOS AO SERVIDOR //

  // Salvar a URL do servidor
  async saveServidor(url_servidor: string): Promise<void> {
    const db = await this.dbPromise;
    const existingServidor = await db.getAll('servidor');// Se houver algum registro, exclua-o
    if (existingServidor && existingServidor.length > 0) {
      await db.clear('servidor');  // Limpa todos os registros do object store 'servidor'
    }
    const servidor = { url_servidor };
    await db.put('servidor', servidor);
  }

  // Obter a URL do servidor
  async getServidor(): Promise<{ url_servidor: string } | null> {
    const db = await this.dbPromise;
    const servidor = await db.getAll('servidor');

    if (servidor && servidor.length > 0) {
      return servidor[0]; // Retorna o primeiro registro encontrado
    } else {
      return null; // Nenhuma URL salva
    }
  }


  // Verificar se a URL do servidor já está salva
  async isServidorSaved(url_servidor: string): Promise<boolean> {
    const db = await this.dbPromise;
    const servidor = await db.getFromIndex('servidor', 'url_servidor', url_servidor);
    return servidor !== undefined;
  }

  // ---------- TAREFAS ----------
  async saveTarefa(tarefa: Tarefa): Promise<void> {
    const db = await this.dbPromise;
    await db.put('tarefas', { ...tarefa, sincronizado: false });
  }

  async getAllTarefas(): Promise<Tarefa[]> {
    const db = await this.dbPromise;
    return db.getAll('tarefas');
  }

  async getTarefasNaoSincronizadas(): Promise<Tarefa[]> {
    const tarefas = await this.getAllTarefas();
    return tarefas.filter(t =>
      (t.horaInicio && !t.sincronizadoInicio) ||
      (t.horaFim && !t.sincronizadoFim) ||
      (t.motivoCancelamento && !t.sincronizadoCancelamento)
    );
  }

  // Marcar início como sincronizado
  async marcarInicioComoSincronizado(id: number): Promise<void> {
    const db = await this.dbPromise;
    const tarefa = await db.get('tarefas', id);
    if (tarefa) {
      tarefa.sincronizadoInicio = true;
      await db.put('tarefas', tarefa);
    }
  }

  // Marcar fim como sincronizado
  async marcarFimComoSincronizado(id: number): Promise<void> {
    const db = await this.dbPromise;
    const tarefa = await db.get('tarefas', id);
    if (tarefa) {
      tarefa.sincronizadoFim = true;
      await db.put('tarefas', tarefa);
    }
  }

  // Marcar cancelamento como sincronizado
  async marcarCancelamentoComoSincronizado(id: number): Promise<void> {
    const db = await this.dbPromise;
    const tarefa = await db.get('tarefas', id);
    if (tarefa) {
      tarefa.sincronizadoCancelamento = true;
      await db.put('tarefas', tarefa);
    }
  }

  async iniciarTarefaLocal(id: number, horaInicio: string): Promise<void> {
    const db = await this.dbPromise;
    const tarefa = await db.get('tarefas', id);
    if (!tarefa) throw new Error('Tarefa não encontrada');
    tarefa.horaInicio = horaInicio;
    tarefa.sincronizadoInicio = false;
    await db.put('tarefas', tarefa);
  }

  async finalizarTarefaLocal(id: number, horaFim: string, hodometroFinal: number, catracaFinal: number): Promise<void> {
    const db = await this.dbPromise;
    const tarefa = await db.get('tarefas', id);
    if (!tarefa) throw new Error('Tarefa não encontrada');
    tarefa.horaFim = horaFim;
    tarefa.hodometroFinal = hodometroFinal;
    tarefa.catracaFinal = catracaFinal;
    tarefa.sincronizadoFim = false;
    await db.put('tarefas', tarefa);
  }

  async cancelarTarefaLocal(id: number, horaFim: string, motivoCancelamento: string): Promise<void> {
    const db = await this.dbPromise;
    const tarefa = await db.get('tarefas', id);
    if (!tarefa) throw new Error('Tarefa não encontrada');
    tarefa.horaFim = horaFim;
    tarefa.motivoCancelamento = motivoCancelamento;
    tarefa.sincronizadoCancelamento = false;
    await db.put('tarefas', tarefa);
  }

  async saveFolhaServicoComTarefas(folhaServico: FolhaServico, tarefas: Tarefa[]): Promise<void> {
    const db = await this.dbPromise;

    // Filtra apenas tarefas válidas
    const tarefasComId = tarefas.filter(t => t.id != null);

    // Salva cada tarefa individualmente
    for (const tarefa of tarefasComId) {
      await db.put('tarefas', { ...tarefa, sincronizado: false });
    }

    // Salva a folha, incluindo a lista de IDs das tarefas e a matrícula do motorista
    const folhaParaSalvar = {
      ...folhaServico,
      motoristaMatricula: folhaServico.motorista?.matricula ?? 0,
      tarefas: tarefasComId.map(t => t.id),
      sincronizado: false,
      numeroVeiculo: folhaServico.veiculo?.numeroVeiculo || ''
    };

    await db.put('folhasServico', folhaParaSalvar);
  }


  // ---------- JORNADA SERVIÇO UNIFICADO ---------- //
  async iniciarJornadaServicoLocal(jornadaServico: any, offline: boolean = true): Promise<void> {
    const db = await this.dbPromise;
    const registro = {
      ...jornadaServico,
      jornadaInicioSincronizado: jornadaServico.jornadaInicioSincronizado ?? false,
      offline: offline
    };
    await db.put('jornadaServico', registro);
  }


  async finalizarJornadaServicoLocal(id: number, finalizacao: {
    usuario_id: number;
    numero_papeleta: number;
    observacao?: string;
    latitude_fim: number;
    longitude_fim: number;
    hora_final: string;
    folha_servico_id: number;
    horaFim: string;
    latitude_papeleta_fim: number;
    longitude_papeleta_fim: number;
  }, offline: boolean = true
  ): Promise<void> {
    const db = await this.dbPromise;
    const registro = await db.get('jornadaServico', id);
    if (!registro) throw new Error('JornadaServico não encontrada');

    const atualizado = {
      ...registro,
      usuario_id: finalizacao.usuario_id,
      numero_papeleta: finalizacao.numero_papeleta,
      observacao: finalizacao.observacao || '',
      latitude_fim: finalizacao.latitude_fim,
      longitude_fim: finalizacao.longitude_fim,
      hora_final: finalizacao.hora_final,
      folha_servico_id: finalizacao.folha_servico_id,
      horaFim: finalizacao.horaFim,
      latitude_papeleta_fim: finalizacao.latitude_papeleta_fim,
      longitude_papeleta_fim: finalizacao.longitude_papeleta_fim,
      jornadaFimSincronizado: false,
      offline: offline
    };

    await db.put('jornadaServico', atualizado);
  }

  async getJornadaServicosNaoSincronizados(): Promise<any[]> {
    const db = await this.dbPromise;
    const todos = await db.getAll('jornadaServico');
    return todos.filter(js =>
      (!js.jornadaInicioSincronizado) ||
      (js.hasOwnProperty("jornadaFimSincronizado") && !js.jornadaFimSincronizado)
    );
  }

  async marcarInicioJornadaServicoSincronizado(id: number): Promise<void> {
    const db = await this.dbPromise;
    const registro = await db.get('jornadaServico', id);
    if (registro) {
      registro.jornadaInicioSincronizado = true;
      await db.put('jornadaServico', registro);
    }
  }

  async marcarFimJornadaServicoSincronizado(id: number): Promise<void> {
    const db = await this.dbPromise;
    const registro = await db.get('jornadaServico', id);
    if (registro) {
      registro.jornadaFimSincronizado = true;
      await db.put('jornadaServico', registro);
    }
  }

  async getJornadaServicoAberta(folha_servico_id: number): Promise<any | undefined> {
    const db = await this.dbPromise;
    const todas = await db.getAll('jornadaServico');
    return todas.find(j =>
      j.folha_servico_id === folha_servico_id &&
      j.hora_inicial &&
      !j.hora_final
    );
  }

  // Metodo para status de jornada
  async getJornadaServicosLocais(): Promise<any[]> {
    const db = await this.dbPromise;
    return await db.getAll('jornadaServico');
  }

  // Salvar o motorista atual
  async saveMotoristaAtual(motorista: any): Promise<void> {
    const db = await this.dbPromise;
    await db.put('motoristaAtual', { ...motorista, id: motorista.id }); // id fixo
  }

  async getMotoristaAtual(): Promise<any | null> {
    const db = await this.dbPromise;
    const todos = await db.getAll('motoristaAtual');
    return todos.length > 0 ? todos[0] : null;
  }

  // Buscar o motorista atual
  /*async getMotoristaAtual(): Promise<any | null> {
    const db = await this.dbPromise;
    return await db.get('motoristaAtual', 'atual');
  }*/

  async getFolhaServicoPorDataEMatricula(data: string, matricula: number): Promise<any | null> {
    const db = await this.dbPromise;
    const folhas = await db.getAll('folhasServico');
    return folhas.find(f => f.dataServico === data && f.motoristaMatricula === matricula) || null;
  }

  async getTarefasPorIds(ids: number[]): Promise<any[]> {
    const db = await this.dbPromise;
    const tarefas = await db.getAll('tarefas');
    return tarefas.filter(t => ids.includes(t.id));
  }

  async getTarefasDaFolha(folhaId: number): Promise<Tarefa[]> {
    const db = await this.dbPromise;
    const todas = await db.getAll('tarefas');
    return todas.filter(t => t.folhaServicoId === folhaId);
  }

  //Metodos relacionados a posicionamento

  // SALVAR posicionamento localmente
  async savePosicionamentoLocal(pos: any): Promise<void> {
    const db = await this.dbPromise;
    const registro = {
      ...pos,
      sincronizado: pos.sincronizado ?? false,
      tentativaEnvio: pos.tentativaEnvio ?? 0,
      criadoEm: pos.criadoEm ?? Date.now()
    };
    await db.put('posicionamentos', registro);
  }

  // PEGAR todos não sincronizados
  async getPosicionamentosNaoSincronizados(): Promise<any[]> {
    const db = await this.dbPromise;
    const todos = await db.getAll('posicionamentos');
    return todos.filter(p => !p.sincronizado);
  }

  // MARCAR sincronizado
  async marcarPosicionamentoComoSincronizado(id_local: string): Promise<void> {
    const db = await this.dbPromise;
    const reg = await db.get('posicionamentos', id_local);
    if (reg) {
      reg.sincronizado = true;
      await db.put('posicionamentos', reg);
    }
  }

  //DELETAR após sincronizado
  async removerPosicionamento(id_local: string): Promise<void> {
    const db = await this.dbPromise;
    await db.delete('posicionamentos', id_local);
  }

  // NOVOS MÉTODOS PARA CONTROLE DE REGISTRO DE JORNADA
  async salvarUltimoRegistroJornada(timestamp: number): Promise<void> {
    const db = await this.dbPromise;
    await db.put('controleRegistros', { 
      id: 'ultimoRegistroJornada', 
      timestamp: timestamp 
    });
  }

  async buscarUltimoRegistroJornada(): Promise<number | null> {
    const db = await this.dbPromise;
    const registro = await db.get('controleRegistros', 'ultimoRegistroJornada');
    return registro ? registro.timestamp : null;
  }

  // MÉTODO PARA LIMPAR REGISTRO 
  async limparUltimoRegistroJornada(): Promise<void> {
    const db = await this.dbPromise;
    await db.delete('controleRegistros', 'ultimoRegistroJornada');
  }

}
